import type { AttendanceHistoryItem } from '../types/attendance';
import type { HolidayRecord } from '../types/officeCalendar';
import { holidayDateFromApi, isCalendarWeekOff } from './officeCalendar';

export function normalizeHolidayRecords(
  holidays: HolidayRecord[] | undefined | null,
): HolidayRecord[] {
  return (holidays ?? []).map(holiday => ({
    ...holiday,
    holidayDate: holidayDateFromApi(holiday.holidayDate),
  }));
}

export function holidaysByDate(
  holidays: HolidayRecord[],
): Map<string, HolidayRecord> {
  return new Map(
    normalizeHolidayRecords(holidays).map(holiday => [
      holiday.holidayDate,
      holiday,
    ] as const),
  );
}

export function holidaysInDateRange(
  holidays: HolidayRecord[],
  fromDate: string,
  toDate: string,
): HolidayRecord[] {
  return normalizeHolidayRecords(holidays).filter(
    holiday =>
      holiday.holidayDate >= fromDate && holiday.holidayDate <= toDate,
  );
}

function emptyHistoryItem(
  attendanceDate: string,
  statusCode: string,
  statusLabel: string,
  statusReason: string | null = null,
): AttendanceHistoryItem {
  return {
    attendanceId: null,
    attendanceDate,
    statusCode,
    statusLabel,
    statusReason,
    shiftName: null,
    checkInTime: null,
    checkOutTime: null,
    workedMinutes: null,
    breakMinutes: null,
    lateMinutes: null,
    earlyDepartureMinutes: null,
    overtimeMinutes: null,
    attendanceMode: null,
    isRegularized: null,
  };
}

export function holidayToHistoryItem(
  holiday: HolidayRecord,
): AttendanceHistoryItem {
  if (isCalendarWeekOff(holiday)) {
    return emptyHistoryItem(
      holiday.holidayDate,
      'WEEKLY_OFF',
      'Weekly Off',
      holiday.holidayName,
    );
  }
  return emptyHistoryItem(
    holiday.holidayDate,
    'HOLIDAY',
    'Holiday',
    holiday.holidayName,
  );
}

/**
 * Adds the backend's resolved status (approved leave, LOP, holiday, weekly off)
 * for dates with no attendance record, so leave days are not guessed as LOP.
 * Off days defer to the calendar holiday record, which carries the holiday name.
 */
export function mergeDayStatusItems(
  items: AttendanceHistoryItem[],
  dayItems: AttendanceHistoryItem[],
  holidays: Array<Pick<HolidayRecord, 'holidayDate'>>,
): AttendanceHistoryItem[] {
  const byDate = new Map(
    items.map(item => [item.attendanceDate.slice(0, 10), item] as const),
  );
  const holidayDates = new Set(
    holidays.map(holiday => holidayDateFromApi(holiday.holidayDate)),
  );
  for (const day of dayItems) {
    const date = day.attendanceDate.slice(0, 10);
    if (byDate.has(date)) continue;
    const offDay = day.statusCode === 'HOLIDAY' || day.statusCode === 'WEEKLY_OFF';
    if (offDay && holidayDates.has(date)) continue;
    byDate.set(date, day);
  }
  return [...byDate.values()];
}

/**
 * Adds holiday/week-off rows for dates with no attendance record.
 * Days off come from the calendar database. LOP is never guessed here: only the
 * backend knows about approved leave, so a date it did not resolve stays empty.
 */
export function mergeHolidaysIntoHistoryItems(
  items: AttendanceHistoryItem[],
  holidays: HolidayRecord[],
  fromDate: string,
  toDate: string,
): AttendanceHistoryItem[] {
  const byDate = new Map(
    items.map(item => [item.attendanceDate.slice(0, 10), item] as const),
  );

  for (const holiday of holidaysInDateRange(holidays, fromDate, toDate)) {
    if (!byDate.has(holiday.holidayDate)) {
      byDate.set(holiday.holidayDate, holidayToHistoryItem(holiday));
    }
  }

  return [...byDate.values()].sort((left, right) =>
    right.attendanceDate.localeCompare(left.attendanceDate),
  );
}
